import { memo } from "react";
import type { Phase, ResultItem } from "../../state.js";

interface ResultsGridProps {
  results: ResultItem[];
  phase: Phase;
  onToggleSelect: (id: string) => void;
  onSelectAll: (selected: boolean) => void;
  onDownloadZip: () => void;
  onDownloadOne: (id: string) => void;
  onOpenLightbox: (id: string) => void;
}

export function ResultsGrid({
  results,
  phase,
  onToggleSelect,
  onSelectAll,
  onDownloadZip,
  onDownloadOne,
  onOpenLightbox,
}: ResultsGridProps) {
  if (phase === "idle" && results.length === 0) return null;

  const selectedCount = results.filter((r) => r.selected).length;
  const allSelected = selectedCount === results.length && results.length > 0;

  return (
    <>
      <h2>3. 預覽結果</h2>
      {results.length === 0 ? (
        <div className="empty-state">尚未處理。設定好後按下方「開始處理」按鈕。</div>
      ) : (
        <>
          <div className="results-actions">
            <div>
              <button className="secondary" onClick={() => onSelectAll(!allSelected)}>
                {allSelected ? "全部取消" : "全部選取"}
              </button>
              <span style={{ marginLeft: "0.75rem", color: "var(--fg-muted)" }}>
                {selectedCount} / {results.length} 已選
              </span>
            </div>
            <button
              className="primary"
              disabled={selectedCount === 0}
              onClick={onDownloadZip}
            >
              下載 ZIP ({selectedCount})
            </button>
          </div>
          <div className="results-grid">
            {results.map((r) => (
              <ResultCard
                key={r.id}
                result={r}
                onToggle={onToggleSelect}
                onDownload={onDownloadOne}
                onOpenLightbox={onOpenLightbox}
              />
            ))}
          </div>
        </>
      )}
    </>
  );
}

interface ResultCardProps {
  result: ResultItem;
  onToggle: (id: string) => void;
  onDownload: (id: string) => void;
  onOpenLightbox: (id: string) => void;
}

const ResultCard = memo(function ResultCard({
  result,
  onToggle,
  onDownload,
  onOpenLightbox,
}: ResultCardProps) {
  return (
    <div className={`result-card ${result.selected ? "selected" : ""}`}>
      <input
        type="checkbox"
        className="select-checkbox"
        checked={result.selected}
        aria-label={`選取 ${result.filename}`}
        onChange={() => onToggle(result.id)}
      />
      <button
        type="button"
        className="download-one"
        title="下載此圖"
        aria-label={`下載 ${result.filename}`}
        onClick={(e) => {
          e.stopPropagation();
          onDownload(result.id);
        }}
      >
        ⬇
      </button>
      <div
        className="thumb-wrap"
        role="button"
        tabIndex={0}
        title="點擊放大檢視"
        onClick={() => onOpenLightbox(result.id)}
      >
        <img src={result.thumbUrl} alt={result.filename} loading="lazy" />
      </div>
      <div className="card-body">{result.filename}</div>
    </div>
  );
});
